import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Package } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingCart from "@/components/layout/FloatingCart";
import FloatingWhatsApp from "@/components/layout/FloatingWhatsApp";
import ProductCard from "@/components/product/ProductCard";
import CollectionFilters, { FilterState } from "@/components/collection/CollectionFilters";
import { Input } from "@/components/ui/input";
import { ProductGridSkeleton } from "@/components/product/ProductCardSkeleton";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { useLanguage } from "@/context/LanguageContext";
import SEOHead from "@/components/SEOHead";

const Collection = () => {
  const { data: allProducts = [], isLoading } = useProducts();
  const { data: categoriesData = [], isLoading: categoriesLoading } = useCategories();
  const { t } = useLanguage();

  const categories = [t.common.all, ...categoriesData.map((c) => c.name)];

  const maxPrice = useMemo(() => {
    if (allProducts.length === 0) return 5000;
    return Math.ceil(Math.max(...allProducts.map((p) => p.price)) / 100) * 100;
  }, [allProducts]);

  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    priceRange: [0, maxPrice],
    sort: "newest",
  });

  const [searchQuery, setSearchQuery] = useState("");

  useMemo(() => {
    if (filters.priceRange[1] === 5000 && maxPrice !== 5000) {
      setFilters((prev) => ({ ...prev, priceRange: [prev.priceRange[0], maxPrice] }));
    }
  }, [maxPrice]);

  const filteredProducts = useMemo(() => {
    let result = [...allProducts];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(q) || p.nameArabic?.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
      );
    }
    if (filters.categories.length > 0) {
      result = result.filter((p) =>
        filters.categories.some(cat => p.categories?.includes(cat) || p.category === cat)
      );
    }
    result = result.filter((p) => p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]);
    switch (filters.sort) {
      case "price-asc": result.sort((a, b) => a.price - b.price); break;
      case "price-desc": result.sort((a, b) => b.price - a.price); break;
      case "name-asc": result.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "name-desc": result.sort((a, b) => b.name.localeCompare(a.name)); break;
      case "oldest": result.reverse(); break;
      default: break;
    }
    return result;
  }, [allProducts, filters, searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Perfume Collection"
        description="Browse our full collection of luxury perfumes. Filter by category, price, and more."
        canonical="https://bait-al-teeb-essence.lovable.app/collection"
      />
      <Navbar />
      <FloatingCart />
      <FloatingWhatsApp />
      <main className="pt-32 pb-20">
        <div className="container-luxury">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <span className="text-primary text-sm tracking-[0.3em] uppercase">{t.collection.badge}</span>
            <h1 className="text-5xl md:text-6xl font-serif mt-4 mb-6">{t.collection.title}</h1>
            <div className="divider-gold w-32 mx-auto" />
            <p className="text-muted-foreground mt-6 max-w-xl mx-auto">{t.collection.description}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground rtl:left-auto rtl:right-4" />
              <Input
                type="text"
                placeholder={t.collection.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 rtl:pl-3 rtl:pr-11 h-12 rounded-xl bg-card/60 backdrop-blur-sm border-border/50 focus:border-primary/50 text-sm tracking-wide"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/20 transition-colors rtl:right-auto rtl:left-3">
                  <span className="text-xs text-muted-foreground">✕</span>
                </button>
              )}
            </div>
          </motion.div>

          {(isLoading || categoriesLoading) && (
            <ProductGridSkeleton count={6} />
          )}

          {!isLoading && !categoriesLoading && (
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 lg:gap-10">
              <CollectionFilters categories={categories} filters={filters} onFiltersChange={setFilters} maxPrice={maxPrice} productCount={filteredProducts.length} />
              <div className="flex-1 min-w-0">
                <AnimatePresence mode="wait">
                  {filteredProducts.length > 0 ? (
                    <motion.div key="products" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                      {filteredProducts.map((product, index) => (
                        <ProductCard key={product.id} product={product} index={index} />
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div key="empty" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-24 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-5">
                        <Package className="w-7 h-7 text-muted-foreground" />
                      </div>
                      <h3 className="font-serif text-xl mb-2">{t.collection.noResults}</h3>
                      <p className="text-sm text-muted-foreground mb-5 max-w-sm">{t.collection.noResultsDesc}</p>
                      <button
                        onClick={() => { setFilters({ categories: [], priceRange: [0, maxPrice], sort: "newest" }); setSearchQuery(""); }}
                        className="text-sm text-primary hover:text-primary/80 transition-colors tracking-wider uppercase"
                      >
                        {t.collection.resetFilters}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Collection;
